import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { createContext, useContext, useMemo } from 'react';

export type PageWidth = 'narrow' | 'standard' | 'wide' | 'full';

interface ContentContainerContextValue {
  readonly hasHorizontalGutter: boolean;
}

const ContentContainerContext = createContext<ContentContainerContextValue>({
  hasHorizontalGutter: false,
});

export interface AppContentContainerProps {
  children: React.ReactNode;
  width?: PageWidth;
  /**
   * Whether the container is used as the authenticated application viewport.
   * When true, it applies the responsive vertical page spacing.
   */
  viewport?: boolean;
  /**
   * Whether to apply the responsive horizontal page gutter. Nested containers
   * automatically reuse an ancestor gutter instead of applying a second one.
   */
  gutter?: boolean;
}

/**
 * Canonical responsive content frame.
 *
 * A shell viewport may contain a page-width container. Both are expressed with
 * this primitive, but only the outermost container that requests a gutter owns
 * horizontal padding. This prevents descriptor pages and bespoke pages from
 * accumulating double gutters while preserving centered page-width limits.
 */
export function AppContentContainer({
  children,
  width = 'standard',
  viewport = false,
  gutter = true,
}: AppContentContainerProps) {
  const theme = useTheme();
  const parent = useContext(ContentContainerContext);
  const maxWidth = theme.financeLayout.page.maxWidth[width];
  const gutterSpacing = theme.financeLayout.page.gutter;
  const sectionGap = theme.financeLayout.page.sectionGap;
  const appliesHorizontalGutter = gutter && !parent.hasHorizontalGutter;
  const contextValue = useMemo<ContentContainerContextValue>(
    () => ({
      hasHorizontalGutter: parent.hasHorizontalGutter || appliesHorizontalGutter,
    }),
    [appliesHorizontalGutter, parent.hasHorizontalGutter],
  );

  return (
    <ContentContainerContext.Provider value={contextValue}>
      <Box
        data-content-container="true"
        data-content-gutter={appliesHorizontalGutter ? 'true' : 'false'}
        sx={{
          width: '100%',
          maxWidth,
          mx: 'auto',
          px: appliesHorizontalGutter
            ? {
                xs: gutterSpacing.xs,
                sm: gutterSpacing.sm,
                md: gutterSpacing.md,
                lg: gutterSpacing.lg,
              }
            : 0,
          py: viewport ? sectionGap.comfortable : 0,
        }}
      >
        {children}
      </Box>
    </ContentContainerContext.Provider>
  );
}
